import type { HTMLAttributes } from 'react';
import { PaginationButton } from '../../basic/PaginationButton';
import styles from './Pagination.module.css';

export interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showInfo?: boolean;
  infoText?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showInfo = false,
  infoText,
  className,
  ...props
}: PaginationProps) {
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  // Generar array de números de página a mostrar
  // Por defecto muestra hasta 4 páginas como en el diseño de Figma
  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5; // cantidad de botones visibles (sin contar first/last)

    if (totalPages <= maxVisible + 2) {
      // Mostrar todas si son pocas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    // Siempre incluir primera página
    pages.push(1);

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    // Siempre incluir última página
    pages.push(totalPages);

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const classNames = [styles.container, className].filter(Boolean).join(' ');

  return (
    <div className={classNames} {...props}>
      {showInfo && (
        <span className={styles.info}>
          {infoText || `Mostrando ${(currentPage - 1) * 10 + 1}-${Math.min(currentPage * 10, totalPages * 10)} de ${totalPages * 10} items`}
        </span>
      )}
      <div className={styles.controls}>
        <PaginationButton
          direction="left"
          disabled={currentPage === 1}
          onClick={handlePrevious}
          aria-label="Página anterior"
        />
        {pageNumbers.map((pageNum, index) => (
          pageNum !== '...'
            ? (
              <PaginationButton
                key={pageNum}
                isActive={pageNum === currentPage}
                onClick={() => handlePageClick(pageNum)}
              >
                {pageNum}
              </PaginationButton>
            )
            : <span key={`ellipsis-${index}`} className={styles.ellipsis}>...</span>
        ))}
        <PaginationButton
          direction="right"
          disabled={currentPage === totalPages}
          onClick={handleNext}
          aria-label="Página siguiente"
        />
      </div>
    </div>
  );
}

Pagination.displayName = 'Pagination';
